import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getCall } from "../utils/Network";
import { clearCart } from "../slices/signUp";
import Navbar from "../components/Navbar";
import {
  OrderConfirmationWrapper,
  ConfirmationRow,
  SuccessHeader,
  OrderSummaryCard,
  ReferralSection,
  HowItWorksSection,
  FAQSection,
  ComplianceFooter,
} from "../components/OrderConfirmation";

const ReferralLinkPrefix =
  process.env.REACT_APP_REFERRAL_URL || "https://noaitaly.io";

// ============================================
// Get page configuration with translations
// ============================================
const getPageConfig = (t) => ({
  customer: {
    // Success Header
    title: t("orderConfirmation.customer.title"),
    subtitle: t("orderConfirmation.customer.subtitle"),
    additionalText: "",

    // User ID
    userIdPrefix: "C",

    // CTA
    ctaButtonText: t("orderConfirmation.customer.ctaButtonText"),
    ctaHelperText: t("orderConfirmation.customer.ctaHelperText"),
    ctaIcon: null,

    // Referral Section
    referralBadge: t("orderConfirmation.customer.referralBadge"),
    referralTitle: t("orderConfirmation.customer.referralTitle"),
    referralDescription: t("orderConfirmation.customer.referralDescription"),
    referralLinkPrefix: ReferralLinkPrefix,
    referralVariant: "customer",

    // Social Share
    socialShareDescription: t(
      "orderConfirmation.common.socialShareDescription",
    ),

    // How It Works
    howItWorksTitle: t("orderConfirmation.common.howItWorksTitle"),
    steps: [
      {
        title: t("orderConfirmation.customer.steps.step1Title"),
        description: t("orderConfirmation.customer.steps.step1Description"),
        icon: "share",
      },
      {
        title: t("orderConfirmation.customer.steps.step2Title"),
        description: t("orderConfirmation.customer.steps.step2Description"),
        icon: "users",
      },
      {
        title: t("orderConfirmation.customer.steps.step3Title"),
        description: t("orderConfirmation.customer.steps.step3Description"),
        icon: "star",
        highlight: true,
      },
    ],

    // FAQ
    faqItems: [
      {
        question: t("orderConfirmation.customer.faq.q1"),
        answer: t("orderConfirmation.customer.faq.a1"),
      },
      {
        question: t("orderConfirmation.customer.faq.q2"),
        answer: t("orderConfirmation.customer.faq.a2"),
      },
      {
        question: t("orderConfirmation.customer.faq.q3"),
        answer: t("orderConfirmation.customer.faq.a3"),
      },
      {
        question: t("orderConfirmation.customer.faq.q4"),
        answer: t("orderConfirmation.customer.faq.a4"),
      },
      {
        question: t("orderConfirmation.customer.faq.q5"),
        answer: t("orderConfirmation.customer.faq.a5"),
      },
    ],

    // Compliance
    complianceText: t("orderConfirmation.customer.complianceText"),

    // Page class
    pageClass: "",
  },

  affiliate: {
    // Success Header
    title: t("orderConfirmation.affiliate.title"),
    subtitle: t("orderConfirmation.affiliate.subtitle"),
    additionalText: t("orderConfirmation.affiliate.additionalText"),

    // User ID
    userIdPrefix: "A",

    // CTA
    ctaButtonText: t("orderConfirmation.affiliate.ctaButtonText"),
    ctaHelperText: t("orderConfirmation.affiliate.ctaHelperText"),
    ctaIcon: null,

    // Referral Section
    referralBadge: t("orderConfirmation.affiliate.referralBadge"),
    referralTitle: t("orderConfirmation.affiliate.referralTitle"),
    referralDescription: t("orderConfirmation.affiliate.referralDescription"),
    referralLinkPrefix: ReferralLinkPrefix,
    referralVariant: "affiliate",

    // Social Share
    socialShareDescription: t(
      "orderConfirmation.common.socialShareDescription",
    ),

    // How It Works
    howItWorksTitle: t("orderConfirmation.common.howItWorksTitle"),
    steps: [
      {
        title: t("orderConfirmation.affiliate.steps.step1Title"),
        description: t("orderConfirmation.affiliate.steps.step1Description"),
        icon: "share",
      },
      {
        title: t("orderConfirmation.affiliate.steps.step2Title"),
        description: t("orderConfirmation.affiliate.steps.step2Description"),
        icon: "cart",
      },
      {
        title: t("orderConfirmation.affiliate.steps.step3Title"),
        description: t("orderConfirmation.affiliate.steps.step3Description"),
        icon: "dollar",
        highlight: true,
      },
    ],

    // FAQ
    faqItems: [
      {
        question: t("orderConfirmation.affiliate.faq.q1"),
        answer: t("orderConfirmation.affiliate.faq.a1"),
      },
      {
        question: t("orderConfirmation.affiliate.faq.q2"),
        answer: t("orderConfirmation.affiliate.faq.a2"),
      },
      {
        question: t("orderConfirmation.affiliate.faq.q3"),
        answer: t("orderConfirmation.affiliate.faq.a3"),
      },
      {
        question: t("orderConfirmation.affiliate.faq.q4"),
        answer: t("orderConfirmation.affiliate.faq.a4"),
      },
      {
        question: t("orderConfirmation.affiliate.faq.q5"),
        answer: t("orderConfirmation.affiliate.faq.a5"),
      },
    ],

    // Compliance
    complianceText: t("orderConfirmation.affiliate.complianceText"),

    // Page class
    pageClass: "affiliate-page",
  },
});

// ============================================
// Mock data for development/testing
// ============================================
const MOCK_ORDER_DATA = {
  orderDate: new Date().toISOString(),
  customerId: "123456",
  associateId: "789012",
  subTotal: 159.0,
  tax: 12.72,
  shipping: 0,
  total: 171.72,
  currencyCode: "USD",
  orderDetails: [
    {
      productName: "NOA Premium Membership",
      quantity: 1,
      amount: 159.0,
    },
  ],
};

// ============================================
// Unified Order Confirmation Component
// ============================================
const OrderConfirmation = () => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();
  const [order, setOrder] = useState(null);
  const [webAlias, setWebAlias] = useState("");
  const token = useSelector((state) => state.signup.token);

  // Use mock data when API fails (for previewing the page)
  const displayOrder = order || MOCK_ORDER_DATA;
  const useMockData = !order;

  // Determine page type from URL param (defaults to "customer")
  const pageType = params.type === "affiliate" ? "affiliate" : "customer";
  const PAGE_CONFIG = getPageConfig(t);
  const config = PAGE_CONFIG[pageType];

  // Generate referral link: https://joinnoa.io/{username}/enroll
  const referralLink = webAlias
    ? `${config.referralLinkPrefix}/${webAlias}/enroll`
    : `${config.referralLinkPrefix}/enroll`;

  useEffect(() => {
    const html = document.getElementsByTagName("html")[0];
    html.setAttribute("data-wf-page", "68603f7a056ff6f78f58cb16");
    html.setAttribute("data-wf-site", "68603f7a056ff6f78f58cab1");
    window.Webflow && window.Webflow.destroy();
    window.Webflow && window.Webflow.ready();
    window.Webflow && window.Webflow.require("ix2").init();
    document.dispatchEvent(new Event("readystatechange"));
  }, []);

  useEffect(() => {
    dispatch(clearCart());
  }, [dispatch]);

  // Get webAlias from sessionStorage (set during order creation in Review.js)
  useEffect(() => {
    const storedAlias = sessionStorage.getItem("referralWebAlias");
    if (storedAlias) {
      setWebAlias(storedAlias);
    }
  }, []);

  useEffect(() => {
    async function fetchOrder(id, authToken) {
      try {
        const response = await getCall({
          url: "getCompleteOrderDetails",
          urlParams: { orderNumber: id },
          header: { Authorization: "Bearer " + authToken },
        });
        if (response?.data) {
          console.log("Order data from API:", response.data);
          setOrder(response.data);
        }
      } catch (error) {
        console.log("Order fetch failed, using mock data for preview");
      }
    }

    if (params.orderId) {
      fetchOrder(params.orderId, token);
    }
  }, [params.orderId, token]);

  return (
    <div
      className={`theme-bg ${config.pageClass}`}
      style={{ display: "block", alignItems: "unset" }}
    >
      <Navbar />
      <OrderConfirmationWrapper>
        {/* Success Header */}
        <SuccessHeader
          title={config.title}
          subtitle={config.subtitle}
          orderNumber={params.orderId}
          orderDate={displayOrder?.orderDate}
        />

        {/* Order Summary + Referral Side by Side */}
        <ConfirmationRow>
          {/* Order Summary Card */}
          <OrderSummaryCard
            orderId={params.orderId}
            orderDate={displayOrder?.orderDate}
            userId={displayOrder?.associateId || displayOrder?.customerId}
            userIdPrefix={config.userIdPrefix}
            packageName={displayOrder?.orderDetails?.[0]?.productName}
            imageUrl={displayOrder?.orderDetails?.[0]?.imageUrl}
            price={displayOrder?.subTotal}
            discount={
              displayOrder?.discount ||
              displayOrder?.discountTotal ||
              displayOrder?.discountAmount ||
              0
            }
            tax={displayOrder?.tax}
            total={displayOrder?.total}
            currencyCode={displayOrder?.currencyCode}
            footerText={
              useMockData
                ? t("orderConfirmation.common.previewFooterText")
                : t("orderConfirmation.common.confirmationFooterText")
            }
          />

          {/* Referral Section with Inline Social Share */}
          <ReferralSection
            badge={config.referralBadge}
            title={config.referralTitle}
            description={config.referralDescription}
            referralLink={referralLink}
            variant={config.referralVariant}
            showInlineSocial={true}
          />
        </ConfirmationRow>

        {/* How It Works */}
        <HowItWorksSection
          title={config.howItWorksTitle}
          steps={config.steps}
        />

        {/* FAQ Section */}
        <FAQSection
          title={t("orderConfirmation.common.faqTitle")}
          items={config.faqItems}
        />

        {/* Compliance Footer */}
        <ComplianceFooter text={config.complianceText} />
      </OrderConfirmationWrapper>
    </div>
  );
};

export default OrderConfirmation;
